"use client";

import {JSX, useMemo} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {Select, type SelectOption} from "@/shared/models/form/select";
import {Typography} from "@/shared/ui/data-display/typography";
import {SelectField} from "@/shared/ui/form/select/select-field";
import {ContainerBase} from "@/shared/ui/layout/container-base";

import "./styles/location-navigator.scss";

export interface LocationNavigatorSelect {
    id: string;
    label: string;
    placeholder: string;
    image?: string;
    options: Array<SelectOption>;
}

export interface LocationNavigatorProps {
    title?: string;
    selects?: Array<LocationNavigatorSelect>;
    space?: SpaceProps;
    anchorId?: string;
}

export function LocationNavigator({title, selects = [], space, anchorId}: LocationNavigatorProps): JSX.Element | null {
    const sectionVars = useSectionSpace(space);
    const selectModels = useMemo(() => {
        return selects.map((selectConfig) => {
            return Select({
                options: selectConfig.options,
                isSearchable: true,
                isClearable: true,
            });
        });
    }, [selects]);

    if (selects.length === 0) {
        return null;
    }

    return (
        <section className="location-navigator" style={sectionVars} id={anchorId}>
            <ContainerBase>
                {title && (
                    <Typography
                        as="h2"
                        size={{desktop: 24, mobile: 18}}
                        weight={600}
                        color="title"
                        lineHeight={150}
                        className="location-navigator__title"
                    >
                        {title}
                    </Typography>
                )}
                <div className="location-navigator__selects">
                    {selects.map((selectConfig, index) => (
                        <SelectField
                            key={selectConfig.id}
                            model={selectModels[index]}
                            label={selectConfig.label}
                            placeholder={selectConfig.placeholder}
                            className="location-navigator__select"
                            icon={
                                selectConfig.image ? (
                                    <SmartImage
                                        src={selectConfig.image}
                                        alt={selectConfig.placeholder}
                                        width={40}
                                        height={40}
                                        className="location-navigator__image"
                                    />
                                ) : undefined
                            }
                        />
                    ))}
                </div>
            </ContainerBase>
        </section>
    );
}
